// Client-side mirror of app/lib/chain_address.rb. Purely a UX fast-path: the
// server revalidates every refund address and is the authority. Keep the two in
// sync — a rule that exists only here is not a rule.
//
// Checksums are deliberately NOT verified in the browser (EIP-55 needs keccak,
// Tron needs base58check + sha256). Shape and cross-chain mistakes are what a
// user actually hits while typing; the server catches the rest.

export type RefundChain = "ethereum" | "solana" | "tron" | null;

const ETH_RE = /^0x[0-9a-fA-F]{40}$/;
// Solana pubkeys are 32-byte ed25519 keys → 32–44 base58 chars (no 0,O,I,l).
const SOL_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
// Tron mainnet base58check addresses start with T and are 34 chars.
const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

export const chainForAsset = (assetCode: string): RefundChain => {
  if (assetCode.startsWith("SOL_") || assetCode.endsWith("_SOL")) return "solana";
  if (assetCode.endsWith("_TRON")) return "tron";
  if (assetCode.endsWith("_ETH")) return "ethereum";
  return null;
};

const CHAIN_LABELS: Record<Exclude<RefundChain, null>, string> = {
  ethereum: "Ethereum (0x…)",
  solana:   "Solana",
  tron:     "Tron (T…)"
};

/** Returns null when the address looks usable, otherwise a message to show. */
export const refundAddressError = (
  assetCode: string,
  address: string
): string | null => {
  const addr = address.trim();
  if (!addr) return "Enter a refund address.";

  const chain = chainForAsset(assetCode);
  if (!chain) return null; // unknown asset — let the server decide

  const matches =
    chain === "ethereum" ? ETH_RE.test(addr)
    : chain === "solana" ? SOL_RE.test(addr) && !ETH_RE.test(addr) && !TRON_RE.test(addr)
    : TRON_RE.test(addr);

  if (matches) return null;

  // Name the chain they appear to have pasted — the common mistake is grabbing
  // an address from the wrong wallet tab.
  const looksLike = ETH_RE.test(addr)
    ? "an Ethereum"
    : TRON_RE.test(addr)
    ? "a Tron"
    : SOL_RE.test(addr)
    ? "a Solana"
    : null;

  return looksLike
    ? `That looks like ${looksLike} address. This refund must go to ${CHAIN_LABELS[chain]}.`
    : `Enter a valid ${CHAIN_LABELS[chain]} address.`;
};
